/**
 * Filter-state hook for the products listing. Manages the full set of
 * filter/sort inputs + their URL mirroring and exposes a toggle helper
 * for each.
 *
 * Category URL contract (Wave 2 of the categories rollout — task #18):
 * - Writes use the new `categorySlug` multi-value param.
 * - Reads accept either `categorySlug` OR the legacy `category` param for
 *   back-compat with existing external links.
 * - Consumers should treat selected values as slugs. The backend accepts
 *   both slug + name so pre-Wave-2 URLs (`?category=Fresh`) still return
 *   results.
 *
 * Note: the old SSR category landing routes (`/products/<slug>`,
 * `/products/c/<slug>`) were removed when the catalogue was consolidated onto a
 * single filterable `/products` (those URLs now 308-redirect with the
 * collection applied as `?categorySlug=`). The `initialCategorySlug` option is
 * kept only for prop compatibility and no longer seeds filter state.
 */

"use client";

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import type { DeliveryTiming } from "@/components/products/FilterSections/DeliveryTimingSection";
import {
  VALID_SORTS,
  type ProductSort,
} from "@/components/products/FilterSections/SortSection";

function writeUrl(mutate: (u: URL) => void) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  mutate(url);
  window.history.replaceState(null, "", url.pathname + url.search);
}

export interface UseProductFiltersOptions {
  /**
   * @deprecated Kept for prop compatibility but no longer used to seed
   * filter state. Collection scope on `/products/<slug>` pages is enforced
   * via `useProductsQuery({ lockedCategorySlug })` instead, which keeps
   * the user-facing filter UI clean (no phantom active filter on land).
   */
  initialCategorySlug?: string;
}

export interface UseProductFiltersResult {
  categoriesSelected: string[];
  toggleCategory: (c: string) => void;
  /**
   * Replace the category selection wholesale. Used by the hierarchical
   * category filter tree when a parent checkbox toggles every child in one
   * operation.
   */
  setCategoriesSelected: (next: string[]) => void;

  deliveryTiming: DeliveryTiming;
  setDeliveryTiming: (v: DeliveryTiming) => void;

  occasionsSelected: string[];
  toggleOccasion: (o: string) => void;

  coloursSelected: string[];
  toggleColour: (c: string) => void;

  /** Flower/plant-type facet (`?flowers=`) — mirrors the colour facet. */
  flowersSelected: string[];
  toggleFlower: (f: string) => void;

  minPrice: string;
  maxPrice: string;
  setPriceRange: (min: string, max: string) => void;

  sort: ProductSort;
  setSort: (s: ProductSort) => void;

  hasActiveFilters: boolean;
  activeFilterCount: number;
  clearAllFilters: () => void;

  openSections: Set<string>;
  toggleSection: (title: string) => void;
}

export function useProductFilters(
  _options: UseProductFiltersOptions = {}
): UseProductFiltersResult {
  const searchParams = useSearchParams();

  const [categoriesSelected, setCategoriesSelectedState] = useState<string[]>(
    () => {
      // Primary: new `categorySlug` multi-value param.
      const slugParams = searchParams.getAll("categorySlug");
      if (slugParams.length > 0) return slugParams;
      // Back-compat: legacy `category` multi-value (used by pre-Wave-2 links
      // and by the admin filter UI which still writes `category`).
      const legacy = searchParams.getAll("category");
      if (legacy.length > 0) return legacy;
      // NOTE: We deliberately DO NOT seed from `initialCategorySlug` here.
      // On `/products/<slug>` pages the collection scope is enforced via
      // `lockedCategorySlug` in `useProductsQuery` — making it the API-side
      // truth. Seeding the user-facing state would falsely flag the page
      // as "1 filter active" on first land (Clear-all button visible,
      // count badge in the filter sheet trigger) even though the user
      // hasn't touched anything.
      return [];
    }
  );
  const [deliveryTiming, setDeliveryTiming] = useState<DeliveryTiming>("");
  // Seed occasion / colour / price from the URL too (mirrors category + sort above)
  // so deep links like /products?occasion=Birthday&colour=Pink&maxPrice=30 land with
  // those filters already applied — used by the chat assistant's "View all" link.
  const [occasionsSelected, setOccasionsSelected] = useState<string[]>(
    () => searchParams.getAll("occasion")
  );
  const [coloursSelected, setColoursSelected] = useState<string[]>(
    () => searchParams.getAll("colour")
  );
  // Flower/plant-type facet. Backed by real product data (`products.flowers`),
  // surfaced by `FlowerSection` and honoured by `GET /api/products?flowers=`.
  const [flowersSelected, setFlowersSelected] = useState<string[]>(
    () => searchParams.getAll("flowers")
  );
  const [minPrice, setMinPrice] = useState(() => searchParams.get("minPrice") ?? "");
  const [maxPrice, setMaxPrice] = useState(() => searchParams.get("maxPrice") ?? "");

  const sortFromUrl = searchParams.get("sort");
  const initialSort: ProductSort =
    sortFromUrl && (VALID_SORTS as readonly string[]).includes(sortFromUrl)
      ? (sortFromUrl as ProductSort)
      : "default";
  const [sort, setSortState] = useState<ProductSort>(initialSort);

  const setSort = useCallback((next: ProductSort) => {
    setSortState(next);
    writeUrl((url) => {
      if (next === "default") url.searchParams.delete("sort");
      else url.searchParams.set("sort", next);
    });
  }, []);

  /** Mirror `next` into the URL as `categorySlug` multi-value, clearing the
   *  legacy `category` param so there's only one source of truth. */
  const syncCategoriesUrl = useCallback((next: string[]) => {
    writeUrl((url) => {
      url.searchParams.delete("categorySlug");
      url.searchParams.delete("category");
      next.forEach((cat) => url.searchParams.append("categorySlug", cat));
    });
  }, []);

  const setCategoriesSelected = useCallback(
    (next: string[]) => {
      // De-dupe while preserving order.
      const deduped = Array.from(new Set(next));
      setCategoriesSelectedState(deduped);
      syncCategoriesUrl(deduped);
    },
    [syncCategoriesUrl]
  );

  const toggleCategory = useCallback(
    (c: string) => {
      // Compute the next selection from current state, then set it and sync
      // the URL — both OUTSIDE any state-updater function. The URL sync calls
      // history.replaceState (which Next.js turns into a router update), and a
      // state updater must stay pure: doing it inside `setState(prev => …)`
      // fired mid-render and tripped React's "Cannot update a component
      // (Router) while rendering ProductsClient" error once the category
      // filter became visible on /products.
      const next =
        c === "all"
          ? []
          : categoriesSelected.includes(c)
            ? categoriesSelected.filter((x) => x !== c)
            : [...categoriesSelected, c];
      setCategoriesSelectedState(next);
      syncCategoriesUrl(next);
    },
    [categoriesSelected, syncCategoriesUrl]
  );

  /**
   * Mirror a multi-value facet into the URL.
   *
   * Occasion / colour / flowers used to be READ from the URL but never
   * WRITTEN, so a filtered view could not be shared, bookmarked or reloaded —
   * and, since the SSR seed is now built FROM the URL, an unmirrored facet
   * would also mean a reload silently served the unfiltered grid. Same
   * `writeUrl` (replaceState) contract as the category mirror, and the same
   * "mutate history in the handler, never inside a state updater" rule.
   */
  const syncMultiParam = useCallback((key: string, next: string[]) => {
    writeUrl((url) => {
      url.searchParams.delete(key);
      next.forEach((v) => url.searchParams.append(key, v));
    });
  }, []);

  const toggleOccasion = useCallback(
    (o: string) => {
      const next =
        o === "all"
          ? []
          : occasionsSelected.includes(o)
            ? occasionsSelected.filter((x) => x !== o)
            : [...occasionsSelected, o];
      setOccasionsSelected(next);
      syncMultiParam("occasion", next);
    },
    [occasionsSelected, syncMultiParam]
  );

  const toggleColour = useCallback(
    (c: string) => {
      const next =
        c === "all"
          ? []
          : coloursSelected.includes(c)
            ? coloursSelected.filter((x) => x !== c)
            : [...coloursSelected, c];
      setColoursSelected(next);
      syncMultiParam("colour", next);
    },
    [coloursSelected, syncMultiParam]
  );

  const toggleFlower = useCallback(
    (f: string) => {
      const next =
        f === "all"
          ? []
          : flowersSelected.includes(f)
            ? flowersSelected.filter((x) => x !== f)
            : [...flowersSelected, f];
      setFlowersSelected(next);
      syncMultiParam("flowers", next);
    },
    [flowersSelected, syncMultiParam]
  );

  const setPriceRange = useCallback((min: string, max: string) => {
    setMinPrice(min);
    setMaxPrice(max);
  }, []);

  const hasActiveFilters =
    categoriesSelected.length > 0 ||
    deliveryTiming !== "" ||
    occasionsSelected.length > 0 ||
    coloursSelected.length > 0 ||
    flowersSelected.length > 0 ||
    minPrice !== "" ||
    maxPrice !== "";

  const activeFilterCount = useMemo(
    () =>
      [
        categoriesSelected.length,
        deliveryTiming ? 1 : 0,
        occasionsSelected.length,
        coloursSelected.length,
        flowersSelected.length,
        minPrice || maxPrice ? 1 : 0,
      ].reduce((a, b) => a + b, 0),
    [
      categoriesSelected.length,
      deliveryTiming,
      occasionsSelected.length,
      coloursSelected.length,
      flowersSelected.length,
      minPrice,
      maxPrice,
    ]
  );

  const clearAllFilters = useCallback(() => {
    setCategoriesSelectedState([]);
    setDeliveryTiming("");
    setOccasionsSelected([]);
    setColoursSelected([]);
    setFlowersSelected([]);
    setMinPrice("");
    setMaxPrice("");
    writeUrl((url) => {
      url.searchParams.delete("categorySlug");
      url.searchParams.delete("category");
      url.searchParams.delete("occasion");
      url.searchParams.delete("colour");
      url.searchParams.delete("flowers");
      // Paging is meaningless against a freshly-cleared filter set.
      url.searchParams.delete("page");
    });
  }, []);

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set(["Sort by", "Product type"]);
    if (categoriesSelected.length > 0) initial.add("Product type");
    if (deliveryTiming) initial.add("How to order");
    if (occasionsSelected.length > 0) initial.add("Occasion");
    if (coloursSelected.length > 0) initial.add("Colour");
    if (flowersSelected.length > 0) initial.add("Flower");
    if (minPrice || maxPrice) initial.add("Price range (£)");
    return initial;
    // Initial-only computation — section-open state is owned by the user after mount.
  });

  const toggleSection = useCallback((title: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  }, []);

  return {
    categoriesSelected,
    toggleCategory,
    setCategoriesSelected,
    deliveryTiming,
    setDeliveryTiming,
    occasionsSelected,
    toggleOccasion,
    coloursSelected,
    toggleColour,
    flowersSelected,
    toggleFlower,
    minPrice,
    maxPrice,
    setPriceRange,
    sort,
    setSort,
    hasActiveFilters,
    activeFilterCount,
    clearAllFilters,
    openSections,
    toggleSection,
  };
}
